import React, { useState, useEffect } from 'react';

const STEPS = [
  { num: '01', title: 'Publiez votre annonce', desc: "Créez votre annonce en 10 minutes. Photos, description, équipements, tarifs — notre assistant vous guide étape par étape.", color: 'var(--green)', bg: 'var(--green-light)' },
  { num: '02', title: 'Emma gère pour vous', desc: "Dès la publication, Emma répond à vos voyageurs en français, anglais, espagnol et allemand. 24h/24, 7j/7.", color: '#534AB7', bg: '#EEEDFE' },
  { num: '03', title: 'Accueillez en confiance', desc: "Photos d'état des lieux horodatées, dépôt de garantie via Stripe, codes d'accès envoyés automatiquement.", color: 'var(--terra-mid)', bg: 'var(--terra-light)' },
  { num: '04', title: 'Encaissez sous 24h', desc: "Versement sur votre compte 24h après le check-in. Commission de 8% à vie pour les 50 premiers hôtes fondateurs, ou 8% les 3 premiers mois puis 12% pour les suivants.", color: 'var(--ink)', bg: 'var(--surface-2)' },
];

const ADVANTAGES = [
  { title: 'Commission 8% à vie pour les 50 premiers', desc: "Puis 12% — contre 15,5% chez Airbnb et 17% chez Booking. Les hôtes fondateurs restent à 8% à vie.", color: 'var(--green)', bg: 'var(--green-light)', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
  { title: 'Emma IA incluse', desc: "Répond à vos voyageurs en 30 secondes dans leur langue. Zéro effort de votre côté.", color: '#534AB7', bg: '#EEEDFE', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
  { title: 'Versement sous 24h', desc: "Après le check-in de chaque voyageur, votre argent arrive sur votre compte en moins de 24 heures.", color: 'var(--terra-mid)', bg: 'var(--terra-light)', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg> },
  { title: 'Protection complète', desc: "Dépôt de garantie Stripe, photos horodatées, médiation sous 72h. Vous êtes toujours couvert.", color: 'var(--ink)', bg: 'var(--surface-2)', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z"/></svg> },
];

const FAQS = [
  { q: 'Combien de temps pour publier une annonce ?', r: "En moyenne 10 à 15 minutes. Notre formulaire vous guide étape par étape — titre, photos, équipements, tarifs, règles. Une fois soumise, notre équipe vérifie et publie votre annonce sous 24h." },
  { q: "Quelle est la commission Havenly ?", r: "Les 50 premiers hôtes et conciergeries à rejoindre Havenly bénéficient d'un taux de 8% à vie — garanti contractuellement. Pour les hôtes suivants, la commission est de 8% les 3 premiers mois, puis 12%, toujours bien en dessous des 15,5% d'Airbnb ou des 17% de Booking." },
  { q: "Ai-je besoin d'une assurance spéciale ?", r: "Votre assurance habitation classique suffit dans la plupart des cas. Nous vous recommandons cependant d'informer votre assureur de votre activité de location courte durée pour être pleinement couvert en cas de sinistre grave." },
  { q: 'Comment fonctionne le dépôt de garantie ?', r: "Vous choisissez le montant selon le type de logement (entre 30€ et 250€). Stripe bloque ce montant sur la carte du voyageur au check-in. Il est libéré automatiquement sous 48h après le check-out si aucun litige n'est signalé." },
  { q: 'Emma peut-elle gérer tous les messages ?', r: "Emma gère automatiquement les questions fréquentes sur votre logement (équipements, check-in, parking...), le calendrier et l'envoi des codes d'accès. Pour les demandes inhabituelles, elle vous transfère la conversation." },
  { q: 'Puis-je fixer mes propres prix ?', r: "Oui, vous fixez librement votre prix par nuit et vos frais de ménage. Nous vous affichons une fourchette indicative basée sur les logements similaires dans votre ville pour vous aider à vous positionner." },
  { q: "Que se passe-t-il en cas de dégradation ?", r: "Signalez le problème à Havenly dans les 24h après le check-out avec les photos horodatées. Notre équipe arbitre sous 72h. Le montant du dépôt de garantie peut être utilisé pour couvrir les réparations si la responsabilité du voyageur est établie." },
];

export default function BecomeHostPage({ onNavigate }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [simType, setSimType] = useState('Appartement');
  const [simPrice, setSimPrice] = useState(100);
  const [simNights, setSimNights] = useState(15);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const simRevenu = Math.round(simPrice * simNights * 0.92); // 8% commission fondateur / premiers mois
  const simAirbnb = Math.round(simPrice * simNights * 0.845);
  const simDiff = simRevenu - simAirbnb;

  return (
    <div style={{ background: 'var(--white)', minHeight: '100vh', paddingTop: 60 }}>

      {/* HERO */}
      <section style={{ background: 'linear-gradient(135deg, #E8F5F0 0%, #ffffff 50%)', padding: isMobile ? '3rem 1.5rem' : '5rem 2rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 420px', gap: isMobile ? 32 : 60, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-block', background: 'var(--green-light)', color: 'var(--green-mid)', fontSize: 12, fontWeight: 500, padding: '5px 14px', borderRadius: 20, marginBottom: 20 }}>
              Pour les hôtes
            </div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(32px, 5vw, 56px)', letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 20 }}>
              Louez plus,<br />
              <em style={{ color: 'var(--green)', fontStyle: 'italic' }}>gérez moins</em>
            </h1>
            <p style={{ fontSize: isMobile ? 15 : 16, color: 'var(--ink-3)', lineHeight: 1.8, marginBottom: 32 }}>
              Commission 8% à vie pour les 50 premiers puis 12%, Emma IA incluse, versement sous 24h. Rejoignez les hôtes fondateurs Havenly.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => onNavigate('auth', 'host')} style={{ padding: '13px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 28, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                Publier mon annonce →
              </button>
              {!isMobile && (
                <button onClick={() => onNavigate('howitworks')} style={{ padding: '13px 28px', background: 'transparent', color: 'var(--ink)', border: '1px solid var(--border-2)', borderRadius: 28, fontSize: 15, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                  Comment ça marche
                </button>
              )}
            </div>
          </div>

          {/* Carte stats */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 20, padding: isMobile ? 16 : 24, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16, fontWeight: 500 }}>Hôtes Havenly en moyenne</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Revenus mensuels', val: '2 400 €', delta: '+18% vs Airbnb', color: 'var(--green)' },
                { label: "Taux d'occupation", val: '74%', delta: 'Haute saison : 95%', color: '#534AB7' },
                { label: 'Temps de gestion', val: '2h / mois', delta: 'Emma gère le reste', color: 'var(--terra-mid)' },
                { label: 'Note moyenne', val: '4.89', delta: 'Top 10% des hôtes FR', color: 'var(--ink)' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface)', borderRadius: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{s.label}</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 500, color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{s.delta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section style={{ padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(24px, 3.5vw, 40px)', letterSpacing: '-0.8px', marginBottom: 28, textAlign: 'center' }}>
            Pourquoi les hôtes choisissent Havenly
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {ADVANTAGES.map(a => (
              <div key={a.title} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, padding: isMobile ? '16px' : '24px', borderTop: `3px solid ${a.color}`, display: 'flex', gap: isMobile ? 14 : 0, flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'flex-start' : 'stretch' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: a.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: a.color, marginBottom: isMobile ? 0 : 14, flexShrink: 0 }}>
                  {a.icon}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{a.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.65 }}>{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATEUR */}
      <section style={{ padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem', background: 'var(--white)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3.5vw, 38px)', letterSpacing: '-0.8px', marginBottom: 8, textAlign: 'center' }}>
            Estimez vos revenus
          </h2>
          <p style={{ fontSize: 15, color: 'var(--ink-3)', textAlign: 'center', marginBottom: 28 }}>
            Simulez ce que vous pourriez gagner avec Havenly
          </p>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: isMobile ? 20 : 28 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)', display: 'block', marginBottom: 8 }}>Type de logement</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Studio', 'Appartement', 'Maison', 'Villa'].map(t => (
                    <button key={t} onClick={() => setSimType(t)} style={{ padding: '7px 16px', border: `1px solid ${simType === t ? 'var(--green)' : 'var(--border)'}`, borderRadius: 20, background: simType === t ? 'var(--green-light)' : 'var(--white)', color: simType === t ? 'var(--green-mid)' : 'var(--ink-3)', fontSize: 13, fontWeight: simType === t ? 500 : 400, cursor: 'pointer', fontFamily: 'var(--sans)' }}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)' }}>Prix par nuit</label>
                  <span style={{ fontSize: 16, fontWeight: 500 }}>{simPrice} €</span>
                </div>
                <input type="range" min={30} max={500} step={5} value={simPrice} onChange={e => setSimPrice(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--green)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
                  <span>30 €</span><span>500 €</span>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-3)' }}>Nuits louées par mois</label>
                  <span style={{ fontSize: 16, fontWeight: 500 }}>{simNights} nuits</span>
                </div>
                <input type="range" min={1} max={30} step={1} value={simNights} onChange={e => setSimNights(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--green)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
                  <span>1 nuit</span><span>30 nuits</span>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: 'var(--green-light)', borderRadius: 14, padding: isMobile ? '14px 16px' : '18px 20px', borderTop: '3px solid var(--green)' }}>
                <div style={{ fontSize: 11, color: 'var(--green-mid)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Havenly (8% → 12%)</div>
                <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 500, color: 'var(--green-dark)', marginBottom: 4 }}>{simRevenu.toLocaleString('fr-FR')} €</div>
                <div style={{ fontSize: 12, color: 'var(--green-mid)' }}>/ mois net</div>
              </div>
              <div style={{ background: 'var(--surface)', borderRadius: 14, padding: isMobile ? '14px 16px' : '18px 20px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Airbnb (15,5%)</div>
                <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 500, color: 'var(--ink-3)', marginBottom: 4 }}>{simAirbnb.toLocaleString('fr-FR')} €</div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>/ mois net</div>
              </div>
            </div>
            <div style={{ marginTop: 12, background: 'var(--green-light)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', fontSize: isMobile ? 13 : 14, color: 'var(--green-mid)', fontWeight: 500 }}>
              Vous gagnez {simDiff.toLocaleString('fr-FR')} € de plus par mois avec Havenly
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section style={{ padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3.5vw, 38px)', letterSpacing: '-0.8px', marginBottom: 28, textAlign: 'center' }}>
            Démarrez en 4 étapes
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {STEPS.map(step => (
              <div key={step.num} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', borderLeft: `4px solid ${step.color}` }}>
                <div style={{ background: step.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 20, color: step.color }}>{step.num}</span>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{step.title}</h3>
                  <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYNCHRO ICAL — Vous êtes déjà sur Airbnb ? */}
      <section style={{ padding: isMobile ? '2rem 1.5rem' : '3rem 2rem', background: '#F3F7F4' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ background: '#1A1A18', borderRadius: 20, padding: isMobile ? '24px 20px' : '32px 40px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: isMobile ? 20 : 40, alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(29,158,117,0.15)', border: '1px solid rgba(29,158,117,0.3)', borderRadius: 20, padding: '4px 12px', marginBottom: 14 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                <span style={{ fontSize: 11, color: '#9FE1CB', fontWeight: 500 }}>Déjà sur Airbnb ?</span>
              </div>
              <h3 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 20 : 24, color: '#fff', letterSpacing: '-0.5px', marginBottom: 10 }}>
                Synchronisez votre calendrier en 2 minutes.
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: 16, maxWidth: 480 }}>
                Pas besoin de choisir entre Airbnb et Havenly. Importez votre lien iCal Airbnb et vos dates réservées sont automatiquement bloquées sur Havenly — zéro risque de double réservation.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {[
                  'Dates synchronisées automatiquement',
                  'Mise à jour toutes les heures',
                  'Zéro double réservation',
                ].map(t => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                    <span style={{ color: '#1D9E75', fontSize: 10 }}>✓</span> {t}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
              <button onClick={() => onNavigate('auth')}
                style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
                Je crée mon annonce →
              </button>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                Synchro iCal disponible dans le tableau de bord
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HÔTES FONDATEURS */}
      <section style={{ padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem', background: 'var(--white)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: 'var(--green-light)', color: 'var(--green-mid)', fontSize: 12, fontWeight: 500, padding: '5px 14px', borderRadius: 20, marginBottom: 16 }}>
            Lancement en cours
          </div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3.5vw, 38px)', letterSpacing: '-0.8px', marginBottom: 14 }}>
            Soyez parmi les premiers hôtes Havenly
          </h2>
          <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.8, marginBottom: 28, maxWidth: 520, margin: '0 auto 28px' }}>
            Havenly est en phase de lancement. Les premiers hôtes à nous rejoindre bénéficient du statut <strong>Hôte Fondateur</strong> — commission de 8% à vie garantie contractuellement, accès prioritaire aux nouvelles fonctionnalités, et accompagnement direct de l'équipe.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
            {[
              { icon: '🏅', label: '8% à vie', desc: 'Commission fondateur garantie' },
              { icon: '⚡', label: 'Accès prioritaire', desc: 'Nouvelles fonctionnalités en avant-première' },
              { icon: '🤝', label: 'Accompagnement direct', desc: "Ligne directe avec l'équipe Havenly" },
            ].map(b => (
              <div key={b.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{b.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{b.label}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.desc}</div>
              </div>
            ))}
          </div>
          <button onClick={() => onNavigate('fondateurs')} style={{ padding: '13px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 28, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
            Devenir hôte fondateur →
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: isMobile ? '2.5rem 1.5rem' : '4rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 3vw, 36px)', letterSpacing: '-0.5px', marginBottom: 28, textAlign: 'center' }}>
            Questions fréquentes
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink)', textAlign: 'left', gap: 12 }}>
                  <span>{faq.q}</span>
                  <span style={{ fontSize: 18, color: 'var(--ink-3)', flexShrink: 0, transition: 'transform 0.2s', display: 'inline-block', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 18px 16px', fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                    {faq.r}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARRAINAGE */}
      <section style={{ padding: isMobile ? '2rem 1.5rem' : '3rem 2rem', background: 'var(--surface)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 20, background: 'var(--white)', border: '1px solid #E8A87C', borderRadius: 16, padding: isMobile ? 20 : 28 }}>
          <div>
            <div style={{ display: 'inline-block', background: '#FDF0E6', color: '#C4784A', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, marginBottom: 10 }}>Programme de parrainage</div>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 18 : 22, letterSpacing: '-0.5px', marginBottom: 6 }}>
              Vous connaissez un propriétaire ?<br />
              <em style={{ color: '#C4784A', fontStyle: 'italic' }}>Gagnez 30€ en le parrainant</em>
            </h3>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.6 }}>
              Parrainez un hôte, il profite de 10% de commission pendant 3 mois, vous gagnez 30€ de crédit commission.
            </p>
          </div>
          <button onClick={() => onNavigate('referral')} style={{ padding: '12px 24px', background: '#E8A87C', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap', alignSelf: isMobile ? 'flex-start' : 'center' }}>
            En savoir plus →
          </button>
        </div>
      </section>

      {/* CTA FINAL */}
      <section style={{ padding: '4rem 1.5rem', background: 'var(--ink)', textAlign: 'center' }}>
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(24px, 4vw, 44px)', color: '#fff', letterSpacing: '-0.8px', marginBottom: 16 }}>
            Prêt à vous lancer ?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', marginBottom: 32, lineHeight: 1.7 }}>
            Publiez gratuitement votre première annonce. Votre logement en ligne en 10 minutes.
          </p>
          <button onClick={() => onNavigate('auth', 'host')} style={{ padding: '13px 32px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 28, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
            Publier mon annonce gratuitement →
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: 'var(--ink)', borderTop: '0.5px solid rgba(255,255,255,0.08)', padding: '1.5rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 18, color: '#fff' }}><svg viewBox="0 0 210 52" style={{ height: 22, width: 'auto', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="44" fontFamily="Georgia,'Times New Roman',serif" fontSize="52" fontWeight="400" fontStyle="italic" letterSpacing="-2" fill="white">haven<tspan fill="#1D9E75" dx="-3">ly</tspan></text>
          </svg></div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>© 2026 Havenly</span>
            {[
              { label: 'Mentions légales', page: 'mentions' },
              { label: 'CGU', page: 'cgu' },
              { label: 'CGV', page: 'cgv' },
              { label: 'Confidentialité', page: 'confidentialite' },
              { label: 'Cookies', page: 'cookies' },
            ].map(lnk => (
              <span key={lnk.label} onClick={() => onNavigate('legal', lnk.page)}
                style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}>
                {lnk.label}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Support', 'Presse', 'Investisseurs'].map(l => (
              <span key={l} style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>{l}</span>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}
